// Atribuição UTM/Google Ads/orgânica no domínio mundozerokm (e subdomínios) — o
// widget NUNCA grava cookie de atribuição. Ele só valida sinais da própria URL
// do disparo e, dentro do portal, consulta (somente leitura) os cookies utm_*
// que o próprio LP Next já mantém (middleware/UtmTracker do mundo-zero-km-nextjs).
// Ordem: URL UTM > sinal Google Ads > cookie do portal (leitura, fonte de
// verdade quando não há sinal novo na URL) > config do módulo (tier próprio do
// widget, só usado se não houver cookie) > orgânico (calculado só para o envio
// do lead, nunca persistido). Portado de widget-form-mundo-zero-km/src/attributionUtils.js.
import { getGoogleAdsSignal } from "./gclidTracking.js";

const UTM_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const WZK_OWN_DOMAIN = "mundozerokm.com.br";
const DEFAULT_CAMPAIGN = "formulario-site";

/**
 * Verifica se o disparo está ocorrendo no domínio oficial mundozerokm (ou subdomínio).
 * Fora dele (sites de parceiros), a engine de atribuição/cookie não se aplica —
 * ver getDispatchUtms().
 */
export function isMundoZeroKmHost(hostname = typeof window !== "undefined" ? window.location.hostname : "") {
    return hostname === WZK_OWN_DOMAIN || hostname.endsWith(`.${WZK_OWN_DOMAIN}`);
}

function getCookie(name) {
    if (typeof document === "undefined") return "";
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : "";
}

function normalizeReferrer(referrer) {
    if (!referrer) return "";
    try {
        return new URL(referrer).hostname;
    } catch {
        return "";
    }
}

function hasAnyField(block) {
    return UTM_FIELDS.some((field) => block[field]);
}

function readAttributionCookies() {
    return {
        utm_source: getCookie("utm_source"),
        utm_medium: getCookie("utm_medium"),
        utm_campaign: getCookie("utm_campaign"),
        utm_term: getCookie("utm_term"),
        utm_content: getCookie("utm_content"),
    };
}

function forceCampaignFromSlug(block, offerSlug) {
    return { ...block, utm_campaign: offerSlug || DEFAULT_CAMPAIGN };
}

/**
 * Resolve a atribuição no domínio mundozerokm (e subdomínios) a cada carregamento
 * do widget — sem gravar nenhum cookie. O cookie já mantido pelo portal (middleware/
 * UtmTracker do Next) é consultado (leitura) e vence qualquer `options` fixo
 * passado ao widget (config do módulo); só cai para `options`/orgânico quando não
 * há cookie nem sinal na URL. No tier de `options`, `utm_campaign` é sempre o slug
 * da oferta atual (`offerSlug`) — nunca o valor de `options.utmCampaign`.
 */
export function computeAttributionForLoad(urlUtms = {}, options = {}, searchParams = new URLSearchParams(), offerSlug) {
    const hasUrlUtm = UTM_FIELDS.some((field) => searchParams.has(field));
    if (hasUrlUtm) {
        return {
            utm_source: urlUtms.utm_source || "",
            utm_medium: urlUtms.utm_medium || "",
            utm_campaign: urlUtms.utm_campaign || "",
            utm_term: urlUtms.utm_term || "",
            utm_content: urlUtms.utm_content || "",
        };
    }

    const googleAdsSignal = getGoogleAdsSignal(searchParams);
    if (googleAdsSignal.isPaid) {
        return {
            utm_source: "google",
            utm_medium: "cpc",
            utm_campaign: googleAdsSignal.campaignId || "google-ads-sem-campanha",
            utm_term: "",
            utm_content: "",
        };
    }

    // Cookie do portal (mundozerokm ou subdomínio) — consulta somente leitura,
    // é a fonte de verdade quando não há sinal novo na URL/Google Ads.
    const existingUtmSource = getCookie("utm_source");
    if (existingUtmSource !== "") {
        return readAttributionCookies();
    }

    const optionsBlock = {
        utm_source: options.utmSource || "",
        utm_medium: options.utmMedium || "",
        utm_campaign: options.utmCampaign || "",
        utm_term: options.utmTerm || "",
        utm_content: options.utmContent || "",
    };
    if (hasAnyField(optionsBlock)) {
        return forceCampaignFromSlug(optionsBlock, offerSlug);
    }

    return {
        utm_source: "organic",
        utm_medium: normalizeReferrer(typeof document !== "undefined" ? document.referrer : ""),
        utm_campaign: "",
        utm_term: "",
        utm_content: "",
    };
}

/**
 * Transforma a atribuição resolvida (`computeAttributionForLoad`) no payload
 * final enviado ao Motor v2 — mesma regra do Next (build-attribution.ts):
 *   - atribuição paga (utm_source diferente de "" e de "organic") passa direto;
 *   - atribuição orgânica troca utm_source pelo utm_medium (referrer já
 *     resolvido, seja do cookie do portal ou calculado agora) e fixa
 *     utm_medium="organic", além de preencher utm_campaign com o slug da
 *     oferta atual (`offerSlug`), com fallback "formulario-site".
 */
export function buildAttributionPayload(resolved = {}, offerSlug) {
    const isPaid = resolved.utm_source !== "" && resolved.utm_source !== "organic";
    if (isPaid) {
        return resolved;
    }

    return {
        utm_source: resolved.utm_medium || normalizeReferrer(typeof document !== "undefined" ? document.referrer : ""),
        utm_medium: "organic",
        utm_campaign: offerSlug || DEFAULT_CAMPAIGN,
        utm_term: resolved.utm_term,
        utm_content: resolved.utm_content,
    };
}

/**
 * Resolve as UTMs para sites de parceiros (fora do domínio mundozerokm) — sem engine
 * de cookie/orgânico e sem tiers de URL/Google Ads: utm_source, utm_medium e
 * utm_content vêm sempre do que foi configurado em options (config do módulo);
 * utm_term vem sempre da URL do disparo; utm_campaign é sempre o slug da oferta
 * atual (`offerSlug`), nunca `options.utmCampaign` nem qualquer valor vindo da URL.
 * Campos vazios de source/medium/content viram "N/A" ao enviar ao motor, em formApi.js.
 */
export function getDispatchUtms(urlUtms = {}, options = {}, searchParams = new URLSearchParams(), offerSlug) {
    return {
        utm_source: options.utmSource || "",
        utm_medium: options.utmMedium || "",
        utm_term: urlUtms.utm_term || "",
        utm_content: options.utmContent || "",
        utm_campaign: offerSlug || DEFAULT_CAMPAIGN,
    };
}
